import { InvestmentCard } from "@/components/investment/v2";
import { Box, HStack, VStack } from "@chakra-ui/react";
import { INVESTOR_TYPE_DISPLAY } from "@equidefi/shared/constants/offerings";
import type { DTO } from "@equidefi/shared/types";
import { Heading, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";
import type React from "react";
import { useMemo } from "react";

interface VaultGroupCardProps {
  investments: DTO.Investment[];
  onInvestmentClick: (investmentId: string) => void;
}

/**
 * VaultGroupCard - Card showing investments grouped by vault record
 *
 * Features:
 * - Shows account type icon and name (Individual, Joint, Trust, Entity)
 * - Shows account holder name (from vault)
 * - Lists all investments in this vault group with dark investment cards
 * - Each investment shows offering logo, name, amount, shares, and status badge
 */
export const VaultGroupCard: React.FC<VaultGroupCardProps> = ({ investments, onInvestmentClick }) => {
  // Get vault info from first investment (all should have same vault)
  const vault = investments[0]?.vault;

  /**
   * Get account type display name and icon
   */
  const accountTypeInfo = useMemo(() => {
    const accountType = vault?.ta_account_type || "INDIVIDUAL";
    const displayName = INVESTOR_TYPE_DISPLAY[accountType] || accountType;

    let icon: React.ReactNode;
    switch (accountType) {
      case "INDIVIDUAL":
        icon = <Icon.User size="1.2em" />;
        break;
      case "JOINT":
        icon = <Icon.Users size="1.2em" />;
        break;
      case "TRUST":
        icon = <Icon.FileText size="1.2em" />;
        break;
      case "ENTITY":
        icon = <Icon.Briefcase size="1.2em" />;
        break;
      default:
        icon = <Icon.User size="1.2em" />;
    }

    return { displayName, icon };
  }, [vault]);

  /**
   * Get account holder name
   */
  const accountHolderName = vault?.display_name || "Account";

  return (
    <Box bg="white" borderRadius="lg" p="4" boxShadow="sm" border="2px" borderColor="purple.400">
      {/* Account Type Header */}
      <HStack mb="4" spacing="2">
        <Box color="gray.600">{accountTypeInfo.icon}</Box>
        <VStack align="start" spacing="0" flex="1">
          <Heading as="h3" fontSize="lg" fontWeight="bold" mb="0">
            {accountHolderName}
          </Heading>
          <Text fontSize="sm" mb="0" color="gray.500">
            {accountTypeInfo.displayName}
          </Text>
        </VStack>
      </HStack>

      {/* Investments List - Dark Cards */}
      <VStack spacing="3" align="stretch">
        {investments.map((investment) => (
          <InvestmentCard
            key={investment.id}
            investment={investment}
            cardAction={() => onInvestmentClick(investment.id)}
            hideResumeButton={true}
          />
        ))}
      </VStack>
    </Box>
  );
};
